import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout, Typography, Row, Col, Tabs } from 'antd';
import { ArrowLeft, Volume2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { PreHeader } from '../components/PreHeader';
import { Header } from '../sections/Header';
import { Footer } from '../sections/Footer';
import { AccessibleButton } from '../components/AccessibleButton';
import { AccessibleImage } from '../components/AccessibleImage';
import { db } from '../services/database';
import { standardVariants } from '../utils/useAnimations';
import { useAccessibility } from '../utils/accessibility';
import * as LucideIcons from 'lucide-react';
import type { ParcoursWithRelations } from '../types/database';

const { Content } = Layout;
const { Title, Paragraph } = Typography;

export const ParcoursFichePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { getText } = useAccessibility();

  const [parcours, setParcours] = useState<ParcoursWithRelations | null>(null);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('0');

  useEffect(() => {
    const loadParcours = async () => {
      if (slug) {
        try {
          const parcoursData = await db.getParcoursWithRelationsBySlug(slug);
          if (parcoursData) {
            setParcours(parcoursData);
          }
        } catch (error) {
          console.error('Erreur lors du chargement du parcours:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadParcours();
  }, [slug]);

  const handleToggleMenu = () => {
    setIsMenuVisible(!isMenuVisible);
  };

  const handleToggleFooter = () => {
    setIsFooterVisible(!isFooterVisible);
  };

  const handleBackToParcours = () => {
    navigate('/#parcours');
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleNextParcours = () => {
    // TODO: Logique pour aller au parcours suivant
    navigate('/#parcours');
  };

  const handleReadPage = () => {
    // TODO: Implémenter la lecture de la page
    console.log('Lecture de la page');
  };

  if (loading) {
    return (
      <Layout className="min-h-screen">
        <Content className="flex items-center justify-center">
          <div>Chargement de la fiche...</div>
        </Content>
      </Layout>
    );
  }

  if (!parcours) {
    return (
      <Layout className="min-h-screen">
        <Content className="flex items-center justify-center">
          <div className="text-center">
            <Title level={2}>Fiche de parcours non trouvée</Title>
            <button onClick={handleBackToParcours} className="btn-primary">
              Retour aux parcours
            </button>
          </div>
        </Content>
      </Layout>
    );
  }

  const TypeIcon = LucideIcons[parcours.typesParcours[0]?.picto as keyof typeof LucideIcons] as React.ComponentType<any>;
console.log(parcours?.missions[0].solution.couvertures[0]);

  // Préparer les onglets des missions
  const missionTabs = parcours.missions.map((mission, index) => ({
    key: index.toString(),
    label: `Mission ${mission.numeroMission} : ${mission.nom}`,
    children: (
      <div className="pt-6">
        <div className="bg-neutral-white rounded-2xl border border-neutral-200 p-8">
          <Title level={3} className="!text-2xl !font-bold !mb-6 !text-primary-600">
            Mission {mission.numeroMission} : {mission.nom}
          </Title>
          
          <Row gutter={[60, 40]}>
            <Col xs={24} lg={12}>
              <div className="mb-8">
                <Title level={4} className="!text-lg !font-bold !mb-4">
                  Barrières rencontrées :
                </Title>
                <ul className="space-y-3 text-base leading-relaxed">
                {mission.fiche.barriere.map((e,i)=>{return(<li key={`barriere-${i}`}>• {e}</li>)})}
                </ul>
              </div>
              
              <div>
                <Title level={4} className="!text-lg !font-bold !mb-4">
                  Solutions accessibles :
                </Title>
                <ul className="space-y-3 text-base leading-relaxed">
                  {mission.fiche.solutions.map((e,i)=>{return(<li key={`solutions-${i}`}>• {e}</li>)})}
                </ul>
              </div>
            </Col>
            
            <Col xs={24} lg={12}>
              <div className="rounded-lg h-64 flex items-center justify-center">
                {mission.solution.couvertures && mission.solution.couvertures[0] ? (
                  <AccessibleImage
                    src={mission.solution.couvertures[0]}
                    altKey="images.solutionCover"
                    className="w-full object-cover rounded-lg"
                  />
                ) : (
                  <span className="text-neutral-500">Image d'illustration à venir</span>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </div>
    ),
  }));

  return (
    <Layout className="min-h-screen bg-neutral-white">
      <PreHeader
        onToggleMenu={handleToggleMenu}
        onToggleFooter={handleToggleFooter}
        isMenuVisible={isMenuVisible}
        isFooterVisible={isFooterVisible}
      />

      {isMenuVisible && (
        <div className="my-12"> 
          <Header />
        </div>
      )}

      <Content className={`z-[1] ${isMenuVisible ? 'mt-12' : 'mt-12'}`}>
        {/* Breadcrumb */}
        <div className="px-10 py-4">
          <div className="max-w-[1440px] mx-auto">
            <button
              onClick={handleBackToParcours}
              className="flex items-center gap-2 text-primary-600 hover:text-primary-800 bg-transparent border-none cursor-pointer text-base"
              aria-label={getText('navigation.backToCourses')}
            >
              <ArrowLeft className="w-4 h-4" />
              Retour aux parcours
            </button>
          </div>
        </div>

        {/* Header avec tag et bouton lecture */}
        <motion.div
          id="main-content"
          className="px-10 pt-6 bg-transparent"
          variants={standardVariants}
          initial="hidden"
          animate="visible"
          tabIndex={-1}
        >
          <div className="max-w-[1440px] mx-auto">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className={`tag tag-${parcours.typesParcours[0]?.classe} flex items-center gap-1 text-nowrap mb-0`}>
                  {TypeIcon && <TypeIcon className="w-4 h-4" />}
                  {parcours.typesParcours[0]?.nom}
                </div>
              </div>
              {/* <button
                onClick={handleReadPage}
                className="flex items-center gap-2 text-primary-600 hover:text-primary-800 bg-transparent border-none cursor-pointer text-base font-bold"
                aria-label="Lire le contenu de la page"
              >
                <Volume2 className="w-5 h-5" />
                Lire le contenu de la page
              </button> */}
            </div>
            
            <Title level={1} className="!text-3xl !font-bold !mb-8">
              Fiche du parcours {parcours.id.split("-")[1]} : {parcours.nom}
            </Title>
          </div>
        </motion.div>

        {/* Contenu principal */}
        <div className="px-10 py-6">
          <div className="max-w-[1440px] mx-auto">
            <Row gutter={[80, 40]} className="mb-12">
              {/* Colonne gauche - Explication */}
              <Col xs={24} lg={12}>
                <motion.div
                  variants={standardVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {parcours.fiche.paragraphe.map((e,i)=>{return( <p key={`paragraphe${parcours.id.split("-")[1]}-${i}`} className="!text-lg !leading-relaxed !mb-4" dangerouslySetInnerHTML={{ __html: e }} />)})}                  
                </motion.div>
                
              </Col>

              {/* Colonne droite - Citation */}
              <Col xs={24} lg={12}>
                <motion.div
                  variants={standardVariants}
                  initial="hidden"
                  animate="visible"
                  className="p-8 justify-center items-center"
                >
                  {parcours.fiche.citation &&
                  <Paragraph className="!text-2xl font-bold text-center !text-primary-600 !mb-0 italic">
                    « {parcours.fiche.citation} »
                  </Paragraph>
                  }
                  {
                    parcours.fiche.image && 
                    <AccessibleImage
                      src={parcours.fiche.image}
                      altKey="à définir"
                      className=""
                    />
                  }
                </motion.div>
              </Col>
            </Row>

            {/* Section des missions */}
            <motion.div
              variants={standardVariants}
              initial="hidden"
              animate="visible"
              className="mb-12"
            >
              <Title level={2} className="!text-2xl !font-bold !mb-6">
                {parcours.missions.length>1?"Découvre les explications détaillées à travers les solutions des différentes missions:" : "Découvre les explications détaillées :"}
              </Title>

              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                size="large"
                items={missionTabs}
                className="mission-tabs"
              />
            </motion.div>

            {/* Boutons d'action */}
            <motion.div
              variants={standardVariants}
              initial="hidden"
              animate="visible"
              className="flex gap-6 justify-start mt-4"
            >
              <AccessibleButton
                type="primary"
                onClick={handleNextParcours}
                className="!px-8"
              >
                Passer au parcours suivant
              </AccessibleButton>
              <AccessibleButton
                type="secondary"
                onClick={handleBackToHome}
                className="!px-8"
              >
                Retour à l'accueil
              </AccessibleButton>

            </motion.div>
          </div>
        </div>
      </Content>

      {/* Background décoratif */}
      <div className="blur-2xl overflow-hidden absolute h-full w-full inset-0 z-0 mb-0">
        <img alt='' className="w-[468px] absolute -bottom-60 -left-20 opacity-50" src="/img/svg/shapes/donut.svg" />
        <img alt='' className="w-[468px] absolute -top-[8%] -right-20 opacity-50" src="/img/svg/shapes/half_donut.svg" />
      </div>

      <div className="z-[1]" id="site-footer" tabIndex={-1}>
        {isFooterVisible && (<Footer />)}
      </div>
    </Layout>
  );
};